/**
 * Substantive-change rules: what an answer change invalidates downstream.
 *
 * Some answers early in the form decide what later sections may collect. Changing
 * one after the fact leaves those later answers describing a application the
 * applicant no longer has. This table is the single declarative statement of
 * which answers those are and what each one invalidates; the store applies it and
 * the owning sections detect the change. Nothing else encodes these rules.
 *
 * Two effects, deliberately different in reach:
 *
 *   CLEAR — the collected answers are discarded, because they may not even exist
 *   as options any more. Kept as narrow as possible: wiping biographical data the
 *   applicant typed (employment, references, essays) would be hostile and
 *   pointless, since none of it depends on the program.
 *
 *   RE-VERIFY — completion is dropped for EVERY section after the owning one, so
 *   the applicant has to walk forward through the rest of the form and positively
 *   acknowledge each page. Broader than CLEAR on purpose: an answer that is still
 *   present may still be wrong in the new context, and the walkthrough is how the
 *   applicant notices. This is also required for correctness — `isSectionAccessible`
 *   gates on the FURTHEST completed section, so leaving a later section complete
 *   would keep the whole form reachable and let the applicant walk straight to
 *   submit past the section that was cleared.
 *
 * Only Components of Interest is program-dependent in the collected data. Its
 * catalog is built from program, SLIP track, applicant status, AND citizenship, so
 * every one of those is a trigger. Education & Experience reads the SLIP track too,
 * but its data stays valid — a law degree is a law degree — and only its
 * completeness rules move (the SLIP-Pathways supplement). The re-verify walk covers
 * that on its own, so it is not listed as cleared.
 */

/** A change the applicant can make that invalidates later answers. */
export interface SubstantiveChange {
	/** Stable id, used in the audit event and to de-duplicate a multi-field edit. */
	readonly key: string;
	/** Section that owns the field. Completion is dropped for everything AFTER it. */
	readonly ownerSection: string;
	/** Applicant-facing name for the thing that changed, used in the modal. */
	readonly label: string;
	/** Sections whose collected answers must be discarded. */
	readonly clears: readonly string[];
}

/**
 * Every substantive change, keyed by id.
 *
 * `citizenship` is owned by the Citizenship section (order 3), which sits BEFORE
 * Program Eligibility (order 4) — so changing citizenship also drops Program
 * Eligibility's completion and sends the applicant back through it. That is
 * intended: the eligibility answers are downstream of citizenship.
 */
export const SUBSTANTIVE_CHANGES: Readonly<Record<string, SubstantiveChange>> = {
	program: {
		key: 'program',
		ownerSection: 'programEligibility',
		label: 'the program you are applying to',
		clears: ['componentsOfInterest'],
	},
	slipTrack: {
		key: 'slipTrack',
		ownerSection: 'programEligibility',
		label: 'your SLIP track',
		clears: ['componentsOfInterest'],
	},
	applicantStatus: {
		key: 'applicantStatus',
		ownerSection: 'programEligibility',
		label: 'whether you are a current student or a graduate',
		clears: ['componentsOfInterest'],
	},
	graduationDate: {
		key: 'graduationDate',
		ownerSection: 'programEligibility',
		label: 'your law degree date',
		// Nothing to discard: no section stores answers derived from this date. It
		// changes which questions Education & Experience DISCLOSES (the EPA block),
		// so the re-verify walk is the whole point.
		clears: [],
	},
	citizenship: {
		key: 'citizenship',
		ownerSection: 'citizenship',
		label: 'your citizenship',
		clears: ['componentsOfInterest'],
	},
};

/** Look up a change by key, or undefined when the key is unknown. */
export const substantiveChange = (key: string): SubstantiveChange | undefined => SUBSTANTIVE_CHANGES[key];

/**
 * Collapse a set of simultaneous changes into what actually has to happen.
 *
 * An applicant can change several substantive fields in one save (switching
 * program AND applicant status, say). The union of `clears` applies, and the
 * re-verify walk starts from the EARLIEST owning section so nothing downstream of
 * any change keeps its completion.
 */
export interface CascadePlan {
	/** The changes that fired, in table order. */
	readonly changes: readonly SubstantiveChange[];
	/** Union of every section whose answers are discarded. */
	readonly clears: readonly string[];
	/** Earliest owning section — the anchor for dropping downstream completion. */
	readonly anchorSection: string;
}

/**
 * Build the plan for a set of change keys, or null when nothing applies.
 *
 * `sectionOrder` maps a section key to its position in the form so the earliest
 * owner can be found without this module needing to know the section list.
 */
export function planCascade(changeKeys: readonly string[], sectionOrder: (sectionKey: string) => number): CascadePlan | null {
	const changes = Object.values(SUBSTANTIVE_CHANGES).filter((c) => changeKeys.includes(c.key));
	if (changes.length === 0) return null;

	const clears = [...new Set(changes.flatMap((c) => c.clears))];
	const anchorSection = changes.reduce((earliest, c) =>
		sectionOrder(c.ownerSection) < sectionOrder(earliest.ownerSection) ? c : earliest,
	).ownerSection;

	return { changes, clears, anchorSection };
}

// ─── Applicant-facing confirmation ───────────────────────────────────────────

/** What a cascade would actually cost, measured against the current answers. */
export interface CascadeEffect {
	/** Sections that hold answers today and would lose them. */
	readonly clearing: readonly string[];
	/** Completed sections that would need revisiting. */
	readonly revisiting: readonly string[];
	/** True when the cascade would change nothing worth warning about. */
	readonly isNoop: boolean;
}

/** Join names into readable prose: "A", "A and B", "A, B, and C". */
const listNames = (names: readonly string[]): string => {
	if (names.length <= 1) return names[0] ?? '';
	if (names.length === 2) return `${names[0]} and ${names[1]}`;
	return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

/**
 * Of the sections a change would clear, the ones that actually hold answers.
 *
 * A section only counts as "cleared" if there was something there to lose. On a
 * first pass through the form the applicant is answering these questions for the
 * first time, so a plan may name Components of Interest while Components of
 * Interest is still empty — nothing has been discarded and the applicant must not
 * be told otherwise.
 *
 * Used by BOTH the preview (which decides whether to warn) and the apply (which
 * decides what to mark), so the two can never disagree about what was lost.
 *
 * A section's body key is its section key by convention — `componentsOfInterest`
 * owns `body.componentsOfInterest`.
 */
export function sectionsWithAnswers(sectionKeys: readonly string[], body: Readonly<Record<string, unknown>>): string[] {
	return sectionKeys.filter((key) => {
		const slice = body[key];
		if (slice === undefined || slice === null) return false;
		// An empty array/object is a section that was visited but holds no answers.
		if (Array.isArray(slice)) return slice.length > 0;
		if (typeof slice === 'object') return Object.keys(slice as object).length > 0;
		return true;
	});
}

/**
 * Copy for the confirmation the applicant sees before a substantive change is
 * committed.
 *
 * States the three things they cannot discover on their own: which answers are
 * about to be discarded, that the rest of the form has to be walked again, and —
 * when it applies — that this pulls an already-submitted application back into
 * progress so it must be certified and submitted again. Without that last line an
 * applicant who believes they are finished can silently un-submit themselves.
 */
export function cascadeConfirmCopy(
	plan: CascadePlan,
	effect: CascadeEffect,
	opts: { sectionLabel: (key: string) => string; reopensSubmission: boolean },
): { title: string; message: string; bodyLines: string[]; confirmText: string; cancelText: string; variant: 'warning' } {
	const changed = listNames(plan.changes.map((c) => c.label));
	const bodyLines: string[] = [];

	if (effect.clearing.length > 0) {
		const names = listNames(effect.clearing.map(opts.sectionLabel));
		bodyLines.push(
			effect.clearing.length === 1
				? `Your answers on ${names} will be cleared, because the options there depend on what you just changed. You'll choose again.`
				: `Your answers on ${names} will be cleared, because the options there depend on what you just changed. You'll choose them again.`,
		);
	}

	if (effect.revisiting.length > 0) {
		bodyLines.push(
			`You'll also go back through the remaining ${effect.revisiting.length} section(s) to confirm your answers still apply. Nothing else is deleted — most pages will already be filled in.`,
		);
	}

	if (opts.reopensSubmission) {
		bodyLines.push('This also re-opens your submitted application. You will need to certify and submit it again before the deadline.');
	}

	return {
		title: 'This will clear some of your answers',
		message: `You changed ${changed}.`,
		bodyLines,
		confirmText: 'Yes, make this change',
		cancelText: 'Keep my current answers',
		variant: 'warning',
	};
}
